<template>
  <Toast group="cambio-rappresentante" />
  <ConfirmDialog group="conferma-cambio-rappresentante" :style="{ width: 'min(96vw, 1500px)', maxHeight: '90vh' }">
    <template #message="{ message }">
      <div class="save-confirm-summary">
        <p>Verifica le modifiche prima di confermare.</p>
        <InlineMessage severity="info" class="save-description">Il salvataggio aggiorna il rappresentante solo dei clienti che non lo hanno già e il contatto dei contratti selezionati.</InlineMessage>
        <section>
          <h5>Rappresentante</h5>
          <div class="save-confirm-detail-grid">
            <div><small>Operazione</small><strong>{{ message.rappresentante.azione }}</strong></div>
            <div><small>Nome</small><strong>{{ message.rappresentante.nome }}</strong></div>
            <div><small>CF / Partita IVA</small><strong>{{ message.rappresentante.identificativo }}</strong></div>
          </div>
        </section>
        <section>
          <h5>Contatto</h5>
          <div v-if="message.contatto" class="save-confirm-detail-grid save-confirm-detail-grid--inline">
            <div><small>Operazione</small><strong>{{ message.contatto.azione }}</strong></div>
            <div><small>Indirizzo di spedizione</small><strong>{{ message.contatto.indirizzo }}</strong></div>
            <div><small>Cellulare</small><strong>{{ message.contatto.cell || '—' }}</strong></div>
            <div><small>Email</small><strong>{{ message.contatto.email || '—' }}</strong></div>
            <div><small>PEC</small><strong>{{ message.contatto.pec || (message.contatto.bypassPec ? 'Bypass PEC' : 'Non indicata') }}</strong></div>
            <div v-if="message.contatto.bypassPec && message.contatto.motivazioneBypassPec"><small>Motivazione bypass PEC</small><strong>{{ message.contatto.motivazioneBypassPec }}</strong></div>
          </div>
          <p v-else>Nessun contatto associato: non sono stati selezionati contratti.</p>
        </section>
        <section>
          <h5>Clienti e contratti</h5>
          <div class="save-confirm-clients">
            <div v-for="cliente in message.clienti" :key="cliente.id" class="save-confirm-client">
              <strong>{{ cliente.nome }}</strong>
              <span v-if="cliente.representativeUnchanged">Rappresentante già associato: non verrà modificato.</span>
              <span v-if="cliente.contratti.length">{{ cliente.contratti.length }} contratti da aggiornare</span>
              <div v-if="cliente.contratti.length" class="save-confirm-contracts">
                <Chip v-for="contract in cliente.contratti" :key="contract.id" class="save-confirm-contract-chip" :class="contract.active ? 'is-active' : 'is-ceased'" v-tooltip="contract.active ? null : 'Cessato'">
                  <strong v-if="contract.code_fornitura">{{ contract.code || contract.id }} · {{ contract.code?.slice(-1).toUpperCase() === 'G' ? 'PDR' : 'POD' }}: {{ contract.code_fornitura }}</strong>
                  <span v-else>{{ contract.code || contract.id }}</span>
                </Chip>
              </div>
              <span v-else-if="cliente.representativeUnchanged">Nessun cambio rappresentante e nessun contratto da aggiornare.</span>
              <span v-else-if="cliente.numeroContratti === 0">Solo cambio rappresentante: questo cliente non ha contratti.</span>
              <span v-else>Solo cambio rappresentante: nessun contratto selezionato per il cambio contatto.</span>
            </div>
          </div>
        </section>
        <section v-if="message.documenti.length">
          <h5>Documenti</h5>
          <p v-for="documento in message.documenti" :key="documento">{{ documento }}</p>
        </section>
      </div>
    </template>
  </ConfirmDialog>
  <Dialog :visible="!!saveOutcome" @update:visible="onOutcomeVisibleChange" :header="saveOutcome?.success ? (isStandalone ? 'Rappresentante creato' : 'Cambio rappresentante completato') : (isStandalone ? 'Creazione non riuscita' : 'Cambio rappresentante non riuscito')" modal :closable="true" :style="{ width: 'min(92vw, 520px)' }">
    <div v-if="saveOutcome" class="save-outcome" :role="saveOutcome.success ? 'status' : 'alert'">
      <InlineMessage v-if="saveOutcome.success" severity="success" class="save-outcome-message">Modifiche salvate correttamente.</InlineMessage>
      <InlineMessage v-else severity="error" class="save-outcome-message">{{ saveOutcome.error }}</InlineMessage>
      <p v-if="saveOutcome.success && isStandalone">ID rappresentante: <strong>{{ saveOutcome.rappresentanteId }}</strong></p>
      <InlineMessage v-if="saveOutcome.success && saveOutcome.documentMessage" severity="info">{{ saveOutcome.documentMessage }}</InlineMessage>
      <div v-if="saveOutcome.success && !isStandalone" class="save-outcome-counts">
        <p><strong>{{ saveOutcome.clienti }}</strong> {{ saveOutcome.clienti === 1 ? 'cliente aggiornato' : 'clienti aggiornati' }}</p>
        <p><strong>{{ saveOutcome.contratti }}</strong> {{ saveOutcome.contratti === 1 ? 'contratto aggiornato' : 'contratti aggiornati' }}</p>
      </div>
      <small>La finestra si chiuderà automaticamente tra {{ outcomeSeconds }} secondi.</small>
    </div>
    <template #footer>
      <Button label="Chiudi" icon="pi pi-times" @click="closeOutcome" />
    </template>
  </Dialog>
  <component :is="inline ? 'InlineContainer' : 'Dialog'" :visible="modelValue" @update:visible="onMainVisibleChange" :header="isStandalone ? 'Crea rappresentante' : 'Cambio rappresentante'" modal :class="['cambio-rappresentante-dialog', { 'inline-create-root': inline }]" :closable="!busy" :closeOnEscape="!busy" :style="inline ? { width: '100%', height: '100%' } : { width: isStandalone ? 'min(92vw, 1000px)' : '90vw', maxWidth: isStandalone ? '1000px' : '90vw', height: '90vh', maxHeight: '90vh' }" :contentStyle="{ flex: '1 1 auto', overflowY: 'auto', display: 'flex', paddingTop: 0, paddingBottom: '0.25rem', background: 'var(--surface-50)' }">
    <div class="representative-body" :class="{ 'has-result': searched || creationStep || !isStandalone }" :aria-busy="busy">
      <p class="p-error" v-if="error && !creationStep" role="alert">{{ error }}</p>
      <InlineMessage v-if="embedded && isStandalone && searched" severity="info">Crea il rappresentante e il contatto: dopo il salvataggio sarai abilitato al cambio rappresentante con entrambi già selezionati.</InlineMessage>
      <div class="representative-layout" :class="{ searched: searched || creationStep || !isStandalone, standalone: isStandalone }">
      <div v-if="creationStep" class="creation-step-left"><slot name="creation-step" /></div>
      <RicercaRappresentante v-else-if="!searched" />
      <SelezioneRappresentante v-else-if="!isStandalone" ref="selectionView" />
      <div v-else class="representative-flow" :class="{ 'has-contact-form': newContact, 'searching-representative': !searched }">
      <template v-if="!searched">
        <div class="search-heading">
          <i class="pi pi-user" aria-hidden="true"></i>
          <h4>{{ isStandalone ? 'Verifica il nuovo rappresentante' : 'Cerca il nuovo rappresentante' }}</h4>
          <p>Inserisci il codice fiscale o la partita IVA. La ricerca parte automaticamente al completamento.</p>
        </div>
        <div class="representative-radio-group mb-3">
          <div class="representative-radio">
            <RadioButton inputId="cambio-rapp-fisica" v-model="kind" value="cf" :disabled="busy" />
            <label for="cambio-rapp-fisica" class="m-0">Persona fisica</label>
          </div>
          <div class="representative-radio">
            <RadioButton inputId="cambio-rapp-giuridica" v-model="kind" value="piva" :disabled="busy" />
            <label for="cambio-rapp-giuridica" class="m-0">Persona giuridica</label>
          </div>
        </div>
        <div class="representative-search mx-auto mt-4 mb-3">
          <label for="cambio-rapp-cf">{{ kind === 'cf' ? 'Codice fiscale' : 'Partita IVA' }} <span class="p-error">(obbligatorio)</span></label>
          <InputMask v-if="kind === 'cf'" id="cambio-rapp-cf" v-model="cf" mask="aaaaaa99a99a***a" class="identifier-input uppercase-identifier" :disabled="busy" @complete="onIdentifierComplete" />
          <InputMask v-else id="cambio-rapp-cf" v-model="cf" mask="99999999999" inputmode="numeric" class="identifier-input" :disabled="busy" @complete="onIdentifierComplete" />
          <small v-if="busy" class="block mt-2" role="status">Ricerca del rappresentante in corso...</small>
        </div>
      </template>
      <template v-else>
        <h5 class="section-heading">Rappresentante</h5>
        <div v-if="!representative.id" class="flex justify-content-center mb-3">
          <Button label="Ritorna alla ricerca" icon="pi pi-arrow-up" class="wizard-action return-search" :disabled="busy" @click="embedded ? onCancel() : resetSearch()" />
        </div>
        <div v-if="representative.id" class="representative-card mb-3">
          <Button icon="pi pi-trash" class="remove-representative p-button-rounded p-button-text p-button-danger p-button-sm" aria-label="Ritorna alla ricerca" v-tooltip="'Rimuovi selezionato'" :disabled="busy" @click="resetSearch" />
          <h4>
            <a :href="rappresentanteDetailUrl" target="_blank" rel="noopener noreferrer" class="representative-detail-link" v-tooltip="'Apri dettaglio rappresentante'">{{ representative.name.toUpperCase() }}</a>
          </h4>
          <p>Codice fiscale: <strong>{{ representative.codfis || representative.piva }}</strong></p>
          <p v-if="representative.pec">PEC: <strong>{{ representative.pec }}</strong></p>
        </div>
        <div v-else class="formgrid grid">
          <div class="field col-12">
            <div class="representative-search mx-auto text-center mb-3">
              <label>Codice fiscale / Partita IVA</label>
              <InputText :modelValue="cf" disabled class="w-full uppercase-identifier" />
            </div>
          </div>
          <div v-for="field in representativeFields" :key="field.key" class="field col-12 md:col-6">
            <label :for="`rapp-${field.key}`">{{ field.label }} <span class="p-error">(obbligatorio)</span></label>
            <InputText :id="`rapp-${field.key}`" v-model="representative[field.key]" class="w-full" :disabled="busy" />
          </div>
        </div>
        <h5 class="section-heading">Contatto</h5>
        <p v-if="representative.id && !contacts.length">Nessun contatto valido disponibile: crea un nuovo contatto.</p>
        <div v-if="displayedContacts.length" ref="contactList" class="contact-list">
          <Button v-for="contact in displayedContacts" :key="contact.id" :data-contact-id="contact.id" class="contact-card" :class="{ selected: selectedContact?.id === contact.id || repairSource?.id === contact.id, 'duplicate-contact': duplicateContact?.id === contact.id }" :aria-pressed="selectedContact?.id === contact.id || repairSource?.id === contact.id" :disabled="busy" v-tooltip="duplicateContact?.id === contact.id ? 'Contatto da selezionare' : contact.invalid_fields.length ? 'Completa i dati mancanti o errati per usare questo contatto' : null" @click="selectContact(contact)">
            <span v-if="duplicateContact?.id === contact.id" class="duplicate-contact-badge" aria-hidden="true">&#128073;</span>
            <span class="contact-data-list">
              <span><strong>Indirizzo:</strong> {{ address(contact) }}</span>
              <span v-for="field in invalidAddressFields(contact)" :key="field.key" class="invalid-contact-data"><strong>{{ field.label }}:</strong> {{ contactValue(contact, field.key) }}</span>
              <span v-for="field in communicationFields" :key="field.key" :class="{ 'invalid-contact-data': contact.invalid_fields.includes(field.key) }"><strong>{{ field.label }}:</strong> {{ contactValue(contact, field.key) }}</span>
              <span v-if="contact.pec || contact.invalid_fields.includes('pec')" :class="{ 'invalid-contact-data': contact.invalid_fields.includes('pec') }"><strong>PEC:</strong> {{ contactValue(contact, 'pec') }}</span>
              <span v-if="!contact.pec && contact.bypass_pec" class="bypass-reason"><strong>Motivo bypass PEC:</strong> {{ contact.motivazione_bypass_pec || 'Mancante' }}</span>
            </span>
          </Button>
        </div>
        <div v-if="representative.id" class="flex justify-content-center my-3">
          <Button label="Aggiungi nuovo contatto" icon="pi pi-plus" class="wizard-action p-button-sm" :disabled="busy" @click="startNewContact" />
        </div>
        <div v-if="newContact" class="contact-completion-form formgrid grid mt-3">
          <div class="col-12 contact-form-intro">
            <h5>Indirizzo di spedizione</h5>
            <small v-if="repairSource">Completa i campi evidenziati con dati mancanti o errati.</small>
            <p v-if="duplicateContact" class="p-error duplicate-warning" role="alert">Questo contatto è già presente: seleziona quello indicato. Non è possibile salvarne un duplicato.</p>
            <hr>
          </div>
          <div v-for="field in contactFields" :key="field.key" class="field col-12 md:col-4" :class="{ 'contact-field-to-complete': needsCompletion(field.key) }">
            <label :for="`contact-${field.key}`">{{ field.label }} <span v-if="field.required" class="p-error">(obbligatorio)</span></label>
            <InputText :id="`contact-${field.key}`" v-model="contactForm[field.key]" class="w-full" :disabled="busy || isRepairFieldLocked(field.key)" />
          </div>
          <div class="field col-12 md:col-4" :class="{ 'contact-field-to-complete': needsCompletion('toponimo') }">
            <label for="contact-toponimo">Toponimo <span class="p-error">(obbligatorio)</span></label>
            <Dropdown id="contact-toponimo" v-model="contactForm.toponimo" :options="toponimi" optionLabel="name" optionValue="name" filter class="w-full" :disabled="busy || isRepairFieldLocked('toponimo')" />
          </div>
          <div v-for="field in addressFields" :key="field.key" class="field col-12 md:col-4" :class="{ 'contact-field-to-complete': needsCompletion(field.key) }">
            <label :for="`contact-${field.key}`">{{ field.label }} <span class="p-error">(obbligatorio)</span></label>
            <InputText :id="`contact-${field.key}`" v-model="contactForm[field.key]" class="w-full" :disabled="busy || isRepairFieldLocked(field.key)" />
          </div>
          <div class="field col-12 md:col-4">
            <label for="contact-provincia">Provincia <span class="p-error">(obbligatorio)</span></label>
            <Dropdown id="contact-provincia" v-model="province" :options="provinces" optionLabel="nome" optionValue="sigla" filter class="w-full" :disabled="busy || isRepairFieldLocked('provincia')" @change="contactForm.cod_comune = null; contactForm.cap = ''" />
          </div>
          <div class="field col-12 md:col-4" :class="{ 'contact-field-to-complete': needsCompletion('cod_comune') }">
            <label for="contact-comune">Comune <span class="p-error">(obbligatorio)</span></label>
            <Dropdown id="contact-comune" v-model="contactForm.cod_comune" :options="cities" optionLabel="name" optionValue="id" filter class="w-full" :disabled="busy || !province || isRepairFieldLocked('cod_comune')" @change="setCap" />
          </div>
          <div class="field col-12 md:col-4" :class="{ 'contact-field-to-complete': needsCompletion('cap') }">
            <label for="contact-cap">CAP <span class="p-error">(obbligatorio)</span></label>
            <div v-if="contactCapSettings.isWildcard" class="p-inputgroup">
              <span class="p-inputgroup-addon contact-cap-prefix">{{ contactCapSettings.cap.slice(0, 3) }}</span>
              <input id="contact-cap" :value="contactCapSuffix" type="text" inputmode="numeric" maxlength="2" placeholder="Ultime 2 cifre" aria-label="Ultime due cifre del CAP" class="p-inputtext p-component w-full" :disabled="busy || isRepairFieldLocked('cap')" @input="onCapSuffixInput">
            </div>
            <InputMask v-else :key="contactForm.cod_comune || 'no-city'" id="contact-cap" v-model="contactForm.cap" mask="99999" class="w-full" :disabled="busy || !contactCapSettings.city || contactCapSettings.isFixed || isRepairFieldLocked('cap')" v-tooltip="!contactCapSettings.city ? 'Seleziona prima il comune' : null" />
          </div>
          <div class="field col-12 md:col-4" :class="{ 'contact-field-to-complete': needsCompletion('pec') }">
            <label for="contact-pec">PEC <span v-if="pecRequired && !contactForm.bypass_pec" class="p-error">(obbligatorio)</span></label>
            <InputText id="contact-pec" v-model="contactForm.pec" class="w-full" :disabled="busy || contactForm.bypass_pec || originalPecLocked" />
          </div>
          <div v-if="supportsBypass && !originalPecLocked" class="field col-12 md:col-8">
            <label class="flex gap-2 mb-2"><Checkbox v-model="contactForm.bypass_pec" binary :disabled="busy || isRepairFieldLocked('bypass_pec')" @change="onBypassPecChange" /> Bypass PEC</label>
            <InputText v-if="contactForm.bypass_pec" v-model="contactForm.motivazione_bypass_pec" placeholder="Motivazione obbligatoria" class="w-full" :disabled="busy || isRepairFieldLocked('bypass_pec')" />
          </div>
        </div>
      </template>
      <div v-if="searched" class="document-upload-list">
        <h5 class="section-heading">Documenti</h5>
        <section v-if="!hasIdentityDocument">
          <h5>Documento d’identità <span v-if="identityRequired" class="p-error">(obbligatorio)</span></h5>
          <InlineMessage v-if="isStandalone && !available" severity="info">Salvataggio documento non abilitato.</InlineMessage>
          <template v-if="!isStandalone || available">
            <FileUpload ref="di" :key="`identity-${uploadKey}`" mode="advanced" :multiple="false" :fileLimit="1" accept="application/pdf,image/jpeg,image/png" :maxFileSize="10000000" :showUploadButton="false" :showCancelButton="false" :disabled="busy" chooseLabel="Seleziona file" @select="identityFile = $event.files[0]" @remove="identityFile = null" @clear="identityFile = null" />
            <small>PDF, JPG o PNG, massimo 10 MB.</small>
          </template>
        </section>
        <section v-if="!isStandalone">
          <h5>Documento di nomina <span class="p-error">(obbligatorio)</span></h5>
          <FileUpload ref="nom" :key="uploadKey" mode="advanced" :multiple="false" :fileLimit="1" accept="application/pdf,image/jpeg,image/png" :maxFileSize="10000000" :showUploadButton="false" :showCancelButton="false" :disabled="busy" chooseLabel="Seleziona file" @select="file = $event.files[0]" @remove="file = null" @clear="file = null" />
          <small>PDF, JPG o PNG, massimo 10 MB.</small>
        </section>
      </div>
      </div>
      <ClientiContrattiCambioRappresentante v-if="!isStandalone" />
      </div>
    </div>
    <div v-if="inline" class="inline-create-footer">
      <Button :label="embedded ? 'Torna al cambio' : 'Annulla'" class="p-button-text" :disabled="busy" @click="onCancel" />
      <Button :label="createdResult ? 'Verifica rappresentante' : isStandalone ? 'Crea rappresentante' : 'Salva'" icon="pi pi-save" :loading="busy" :disabled="saveDisabled" @click="confirmSave" />
    </div>
    <template #footer>
      <template v-if="!creationStep && !inline">
        <Button label="Annulla" class="p-button-text" :disabled="busy" @click="onCancel" />
        <Button :label="isStandalone ? 'Crea rappresentante' : 'Salva'" icon="pi pi-save" :loading="busy" :disabled="saveDisabled" @click="confirmSave" />
      </template>
    </template>
  </component>
  <Dialog v-model:visible="contractDialogVisible" :header="`Contratti di ${contractDialogClient?.nome || ''}`" modal :style="{ width: 'min(96vw, 1400px)' }" :contentStyle="{ maxHeight: '80vh', overflowY: 'auto' }">
    <ContractSelectionCards :modelValue="contractDialogSelectedIds" :contracts="contractDialogContracts" :representativeUnchanged="clientHasRepresentative(contractDialogClient)" :disabled="busy" @update:modelValue="updateContractDialogSelection" />
  </Dialog>
</template>

<script>
import { h } from 'vue'
import './RappresentanteDialogPanels.css'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Chip from 'primevue/chip'
import InputText from 'primevue/inputtext'
import InputMask from 'primevue/inputmask'
import RadioButton from 'primevue/radiobutton'
import Dropdown from 'primevue/dropdown'
import Checkbox from 'primevue/checkbox'
import FileUpload from 'primevue/fileupload'
import AutoComplete from 'primevue/autocomplete'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import InlineMessage from 'primevue/inlinemessage'
import provinces from '@/data/province.json'
import CambioRappresentanteService from '@/services/ask-pratiche/cambio-rappresentante'
import RepresentativeDocumentsService from '@/services/ask-pratiche/representative-documents-service'
import ContractSelectionCards from '@/components/ask/details/ContractSelectionCards.vue'
import RicercaRappresentante from './RicercaRappresentante.vue'
import SelezioneRappresentante from './SelezioneRappresentante.vue'
import ClientiContrattiCambioRappresentante from './ClientiContrattiCambioRappresentante.vue'
import { findDuplicateContact } from './contactComparison'

const emptyContact = () => ({ toponimo: '', indirizzo: '', civico: '', cap: '', cod_comune: null, cell: '', email: '', tel: '', pec: '', bypass_pec: false, motivazione_bypass_pec: '' })
const emailValid = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '')
const InlineContainer = { inheritAttrs: false, render() { return h('div', this.$attrs, this.$slots.default?.()) } }
export default {
  components: { RicercaRappresentante, SelezioneRappresentante, ClientiContrattiCambioRappresentante, Dialog, InlineContainer, Button, Chip, InputText, InputMask, RadioButton, Dropdown, Checkbox, FileUpload, AutoComplete, ContractSelectionCards, Toast, ConfirmDialog, InlineMessage },
  props: { modelValue: Boolean, available: Boolean, inline: Boolean, embedded: Boolean, creationStep: Boolean, mode: { type: String, default: 'create' }, initialIdentifier: { type: String, default: '' }, clienti: { type: Array, default: () => [] }, contratti: { type: Array, default: () => [] } },
  emits: ['update:modelValue', 'saved', 'created', 'create-requested'],
  provide() { return { representativeDialog: this } },
  data() {
    return {
      busy: false, error: '', duplicateIdentifier: '', searchInputKey: 0, saveOutcome: null, outcomeSeconds: 10, outcomeTimer: null, kind: 'cf', cf: '', searched: false, representative: {}, contacts: [], selectedContact: null, createdResult: null,
      selectedContractIds: [], selectedClients: [], availableContracts: [], contractDialogClientId: null, clientSearch: null, clientSuggestions: [], clientSearchBusy: false, clientAdding: false, clientSearchSequence: 0,
      newContact: false, repairSource: null, pecBeforeBypass: '', contactForm: emptyContact(), province: null, provinces, toponimi: [], comuni: [], supportsBypass: false, file: null, identityFile: null, hasIdentityDocument: false, uploadKey: 0,
      contactFields: [{ key: 'tel', label: 'Telefono' }, { key: 'cell', label: 'Cellulare', required: true }, { key: 'email', label: 'Email', required: true }],
      addressFields: [{ key: 'indirizzo', label: 'Indirizzo' }, { key: 'civico', label: 'Civico' }],
      communicationFields: [{ key: 'cell', label: 'Cellulare' }, { key: 'email', label: 'Email' }]
    }
  },
  computed: {
    isStandalone() { return this.mode === 'create' },
    rightDisabled() { return this.creationStep || !this.searched || !this.representative.id },
    saveDisabled() { return this.busy || this.clientAdding || (this.isStandalone ? !this.effectiveContact || (this.available && !this.identityFile) : !this.selectedClients.length || ((this.selectedContractIds.length || this.newContact) && !this.effectiveContact) || !this.file || (this.identityRequired && !this.identityFile)) },
    identityRequired() { return !(this.isStandalone && !this.available) && (!this.representative.id || !this.hasIdentityDocument) },
    pecRequired() { return this.isStandalone || this.selectedClients.some(cliente => [1, 3, 4].includes(Number(cliente.cod_type_client))) },
    contractDialogClient() { return this.selectedClients.find(cliente => String(cliente.id) === String(this.contractDialogClientId)) || null },
    contractDialogVisible: {
      get() { return this.contractDialogClientId !== null },
      set(visible) { if (!visible) this.contractDialogClientId = null }
    },
    contractDialogContracts() {
      return this.contractDialogClient ? this.contractsOfClient(this.contractDialogClient.id).map(contract => ({
        ...contract,
        alreadyAssigned: this.contractHasSelectedContact(contract),
        selectable: contract.selectable !== false && !this.contractHasSelectedContact(contract)
      })) : []
    },
    contractDialogSelectedIds() {
      const ids = new Set(this.contractDialogContracts.map(contract => String(contract.id)))
      return this.selectedContractIds.filter(id => ids.has(String(id)))
    },
    rappresentanteDetailUrl() {
      return this.representative.id ? this.$router.resolve({ path: `/ask/rappresentanti/${encodeURIComponent(this.representative.id)}/dettagli` }).href : null
    },
    repairLockedFields() {
      const source = this.repairSource
      if (!source) return []
      const invalid = new Set(source.invalid_fields || [])
      const hasValue = key => !!String(source[key] || '').trim() && !invalid.has(key)
      const city = this.comuni.find(city => String(city.id) === String(source.cod_comune))
      const province = source.provincia || city?.provincia
      const checks = {
        tel: hasValue('tel'),
        cell: hasValue('cell') && /^3\d{8,9}$/.test(source.cell),
        email: hasValue('email') && emailValid(source.email),
        pec: this.originalPecLocked,
        toponimo: hasValue('toponimo') && this.toponimi.some(item => item.name.toUpperCase() === String(source.toponimo).trim().toUpperCase()),
        indirizzo: hasValue('indirizzo'), civico: hasValue('civico'),
        cap: hasValue('cap') && this.validCap(source.cap, source.cod_comune),
        provincia: !invalid.has('provincia') && this.provinces.some(item => item.sigla === province),
        cod_comune: !invalid.has('cod_comune') && !!city && city.provincia === province,
        bypass_pec: this.supportsBypass && !invalid.has('pec') && Number(source.bypass_pec) === 1 && hasValue('motivazione_bypass_pec')
      }
      return Object.keys(checks).filter(key => checks[key])
    },
    originalPecLocked() { return !!this.repairSource && emailValid(this.repairSource.pec) && !this.repairSource.invalid_fields.includes('pec') },
    formContact() { return { ...this.contactForm, cod_rapp: this.representative.id, provincia: this.province, cell: this.contactForm.cell.replace(/[\s-]/g, '') } },
    duplicateContact() { return this.newContact ? findDuplicateContact(this.formContact, this.displayedContacts, this.supportsBypass) || null : null },
    effectiveContact() {
      if (!this.newContact) return this.displayedContacts.find(contact => contact.id === this.selectedContact?.id && contact.selectable) || null
      return this.validNewContact && !this.duplicateContact ? this.formContact : null
    },
    effectiveContactId() { return Number(this.effectiveContact?.id) || null },
    contactCapSettings() {
      const city = this.comuni.find(city => String(city.id) === String(this.contactForm.cod_comune)) || null
      const cap = String(city?.cap || '').trim().toUpperCase()
      return { city, cap, isFixed: /^\d{5}$/.test(cap), isWildcard: /^\d{3}XX$/.test(cap) }
    },
    contactCapSuffix() {
      return this.contactCapSettings.isWildcard && this.contactForm.cap.startsWith(this.contactCapSettings.cap.slice(0, 3)) ? this.contactForm.cap.slice(3) : ''
    },
    displayedContacts() {
      return this.contacts.map(contact => {
        const invalid = new Set(contact.invalid_fields || [])
        for (const key of ['toponimo', 'indirizzo', 'civico']) {
          if (!String(contact[key] || '').trim()) invalid.add(key)
        }
        if (this.toponimi.length && !this.toponimi.some(item => item.name.toUpperCase() === String(contact.toponimo || '').trim().toUpperCase())) invalid.add('toponimo')
        if (!this.validCap(contact.cap, contact.cod_comune)) invalid.add('cap')
        if (this.pecRequired && !emailValid(contact.pec) && !(this.supportsBypass && Number(contact.bypass_pec) === 1 && String(contact.motivazione_bypass_pec || '').trim())) invalid.add('pec')
        if (!this.pecRequired && !String(contact.pec || '').trim()) invalid.delete('pec')
        return { ...contact, invalid_fields: [...invalid], selectable: invalid.size === 0 }
      })
    },
    validIdentifier() { return (this.kind === 'cf' ? /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z][A-Z0-9]{3}[A-Z]$/ : /^\d{11}$/).test(this.cf.trim().toUpperCase()) },
    representativeFields() { return this.kind === 'cf' ? [{ key: 'nome', label: 'Nome' }, { key: 'cognome', label: 'Cognome' }] : [{ key: 'ragione_sociale', label: 'Ragione sociale' }] },
    cities() { return this.comuni.filter(city => city.provincia === this.province) },
    validNewContact() {
      const c = this.contactForm
      const mobile = c.cell.replace(/[\s-]/g, '')
      if (!this.toponimi.some(item => item.name === c.toponimo)) return false
      return ['toponimo', 'indirizzo', 'civico', 'cap', 'cod_comune'].every(key => String(c[key] || '').trim()) && this.validCap(c.cap, c.cod_comune) && /^3\d{8,9}$/.test(mobile) && !/^3(\d)\1+$/.test(mobile) && !['3123456789', '3234567890', '3987654321', '3098765432'].includes(mobile) && emailValid(c.email) && (c.bypass_pec ? this.supportsBypass && !!c.motivazione_bypass_pec.trim() : this.pecRequired ? emailValid(c.pec) : !c.pec || emailValid(c.pec)) && (this.representative.id || this.representativeFields.every(field => String(this.representative[field.key] || '').trim()))
    }
  },
  watch: {
    available(value) { this.documentsService?.setAvailable(value) },
    kind() { this.cf = ''; this.error = ''; this.duplicateIdentifier = '' },
    modelValue(visible) {
      if (visible) { this.clearOutcome(); this.resetSearch(); this.initializeClients(); this.loadOptions(); this.searchInitialIdentifier() }
      else this.clearOutcome()
    },
    duplicateContact(contact) { if (contact) this.$nextTick(() => this.scrollSelectedContact(contact.id)) },
    effectiveContactId() { this.excludeAlreadyAssignedContracts() }
  },
  created() { this.service = new CambioRappresentanteService(this.$http); this.documentsService = new RepresentativeDocumentsService(this.$http); this.documentsService.setAvailable(this.available) },
  mounted() { if (this.modelValue) { this.resetSearch(); this.initializeClients(); this.loadOptions(); this.searchInitialIdentifier() } },
  beforeUnmount() { this.clearOutcome() },
  methods: {
    onMainVisibleChange(visible) { this.$emit('update:modelValue', visible) },
    onCancel() { this.$emit('update:modelValue', false) },
    async useCreatedRepresentative(result) {
      this.cf = result.identifier
      this.kind = /^\d{11}$/.test(result.identifier) ? 'piva' : 'cf'
      await this.$nextTick()
      this.cf = result.identifier
      await this.applySearchResult(result.lookup)
      const contact = this.displayedContacts.find(item => Number(item.id) === Number(result.contatto_id))
      if (!contact) {
        this.selectedContact = null
        this.error = 'Rappresentante creato, ma il contatto appena salvato non è disponibile. Seleziona un contatto per proseguire.'
        return
      }
      this.selectContact(contact)
    },
    async searchInitialIdentifier() {
      if (!this.isStandalone || !this.initialIdentifier) return
      const value = this.initialIdentifier.trim().toUpperCase()
      const kind = /^\d{11}$/.test(value) ? 'piva' : /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z][A-Z0-9]{3}[A-Z]$/.test(value) ? 'cf' : null
      if (!kind) return
      this.kind = kind
      await this.$nextTick()
      if (!this.modelValue) return
      this.cf = value
      await this.search()
    },
    clearOutcome() {
      if (this.outcomeTimer) clearInterval(this.outcomeTimer)
      this.outcomeTimer = null
      this.saveOutcome = null
    },
    showOutcome(outcome) {
      this.clearOutcome()
      this.saveOutcome = outcome
      this.outcomeSeconds = 10
      this.outcomeTimer = setInterval(() => {
        this.outcomeSeconds--
        if (this.outcomeSeconds <= 0) this.closeOutcome()
      }, 1000)
    },
    closeOutcome() {
      this.clearOutcome()
      this.contractDialogClientId = null
      this.$emit('update:modelValue', false)
    },
    onOutcomeVisibleChange(visible) { if (!visible) this.closeOutcome() },
    initializeClients() {
      this.selectedClients = this.clienti.map(cliente => ({ ...cliente }))
      this.availableContracts = this.contratti.map(contract => ({ ...contract }))
      this.selectedContractIds = this.availableContracts.filter(contract => contract.selectable !== false && Number(contract.active) === 1).map(contract => Number(contract.id))
      this.contractDialogClientId = null
      this.clientSearch = null
      this.clientSuggestions = []
      this.clientSearchSequence++
      this.clientSearchBusy = false
      this.clientAdding = false
    },
    async searchClients(event) {
      const query = String(event.query || '').trim()
      const sequence = ++this.clientSearchSequence
      const representativeId = Number(this.representative.id)
      if (query.length < 2) { this.clientSuggestions = []; return }
      this.clientSearchBusy = true
      try {
        const clients = await this.service.searchClients(query, representativeId)
        if (sequence !== this.clientSearchSequence) return
        const selected = new Set(this.selectedClients.map(cliente => String(cliente.id)))
        const seen = new Set()
        this.clientSuggestions = clients
          .filter(cliente => {
            const id = String(cliente.id)
            if (Number(cliente.cod_type_client) === 2 || cliente.cod_type_client == null || Number(cliente.cod_rapp) === representativeId || selected.has(id) || seen.has(id)) return false
            seen.add(id)
            return true
          })
          .slice(0, 25)
          .map(cliente => ({
            ...cliente,
            searchLabel: [
              `${cliente.ragsoc || cliente.code || cliente.id} (${cliente.code || cliente.id})`,
              cliente.codfis && `CF: ${cliente.codfis}`,
              cliente.piva && `P. IVA: ${cliente.piva}`
            ].filter(Boolean).join(' · ')
          }))
      } catch (error) {
        if (sequence === this.clientSearchSequence) this.clientSuggestions = []
      } finally {
        if (sequence === this.clientSearchSequence) this.clientSearchBusy = false
      }
    },
    async addClient(event) {
      const choice = event.value
      this.clientSearch = null
      this.clientSuggestions = []
      if (!choice?.id || this.clientHasRepresentative(choice) || this.selectedClients.some(cliente => String(cliente.id) === String(choice.id))) return
      this.clientAdding = true
      try {
        const preview = await this.service.preview([Number(choice.id)])
        const cliente = preview.clienti?.[0]
        if (!cliente?.id || cliente.cod_type_client == null || Number(cliente.cod_type_client) === 2 || this.clientHasRepresentative(cliente)) {
          throw new Error('Il cliente selezionato è domestico o non è disponibile.')
        }
        if (this.selectedClients.some(selected => String(selected.id) === String(cliente.id))) return
        const name = cliente.ragsoc || choice.ragsoc || String(cliente.id)
        this.selectedClients.push({ id: cliente.id, cod_type_client: cliente.cod_type_client, cod_rapp: cliente.cod_rapp, nome: name, codfis: cliente.codfis, piva: cliente.piva, rappresentante_attuale_id: cliente.rappresentante_attuale_id ?? choice.rappresentante_attuale_id, rappresentante_attuale_nome: cliente.rappresentante_attuale_nome || choice.rappresentante_attuale_nome, rappresentante_attuale_codfis: cliente.rappresentante_attuale_codfis || choice.rappresentante_attuale_codfis })
        const contracts = (preview.contratti || []).map(contract => ({ ...contract, label: Number(contract.active) === 1 ? 'attivo' : 'non attivo', nome_cliente: name }))
        this.availableContracts.push(...contracts)
        this.selectedContractIds.push(...contracts.filter(contract => contract.selectable !== false && Number(contract.active) === 1 && !this.contractHasSelectedContact(contract)).map(contract => Number(contract.id)))
      } catch (error) {
        this.$toast.add({ group: 'cambio-rappresentante', severity: 'error', summary: 'Cliente non aggiunto', detail: error.message || 'Impossibile caricare il cliente.', life: 5000 })
      } finally {
        this.clientAdding = false
      }
    },
    removeClient(id) {
      if (String(this.contractDialogClientId) === String(id)) this.contractDialogClientId = null
      this.selectedClients = this.selectedClients.filter(cliente => String(cliente.id) !== String(id))
      this.availableContracts = this.availableContracts.filter(contract => String(contract.cod_cliente) !== String(id))
      const availableIds = new Set(this.availableContracts.map(contract => String(contract.id)))
      this.selectedContractIds = this.selectedContractIds.filter(contractId => availableIds.has(String(contractId)))
    },
    contractsOfClient(id) { return this.availableContracts.filter(contract => String(contract.cod_cliente) === String(id)) },
    clientHasRepresentative(cliente) {
      if (!cliente) return false
      const currentId = Number(cliente.cod_rapp ?? cliente.info?.cod_rapp ?? cliente.rappresentante_id)
      const targetId = Number(this.representative.id)
      return targetId > 0 && currentId === targetId
    },
    contractHasSelectedContact(contract) { return !!this.effectiveContactId && Number(contract.cod_contatto) === this.effectiveContactId },
    excludeAlreadyAssignedContracts() {
      if (!this.effectiveContactId) return
      const assigned = new Set(this.availableContracts.filter(contract => this.contractHasSelectedContact(contract)).map(contract => String(contract.id)))
      this.selectedContractIds = this.selectedContractIds.filter(id => !assigned.has(String(id)))
    },
    activeContractCount(id) { return this.contractsOfClient(id).filter(contract => Number(contract.active) === 1).length },
    selectedCount(id) {
      const selected = new Set(this.selectedContractIds.map(String))
      return this.contractsOfClient(id).filter(contract => selected.has(String(contract.id))).length
    },
    clientIdentifier(cliente) { return [cliente.codfis, cliente.piva].filter(Boolean).filter((value, index, values) => values.indexOf(value) === index).join(' / ') || 'Non indicato' },
    updateContractDialogSelection(ids) {
      const clientIds = new Set(this.contractDialogContracts.map(contract => String(contract.id)))
      const selectableIds = new Set(this.contractDialogContracts.filter(contract => contract.selectable !== false).map(contract => String(contract.id)))
      this.selectedContractIds = [...this.selectedContractIds.filter(id => !clientIds.has(String(id))), ...ids.filter(id => selectableIds.has(String(id)))]
    },
    isRepairFieldLocked(key) { return this.repairLockedFields.includes(key) },
    onIdentifierComplete(event) { this.cf = (event.target.value || '').toUpperCase(); this.search() },
    resetSearch() { this.error = ''; this.duplicateIdentifier = ''; this.cf = ''; this.searched = false; this.representative = {}; this.contacts = []; this.selectedContact = null; this.createdResult = null; this.repairSource = null; this.pecBeforeBypass = ''; this.newContact = false; this.contactForm = emptyContact(); this.file = null; this.identityFile = null; this.hasIdentityDocument = false; this.province = null; this.uploadKey++; this.searchInputKey++ },
    async loadOptions() { try { const options = await this.service.options(); this.toponimi = options.toponimi; this.comuni = options.comuni } catch (e) { this.error = 'Impossibile caricare le opzioni degli indirizzi.' } },
    async search(allowCreate = true) {
      if (!this.validIdentifier || this.busy) return
      this.busy = true; this.error = ''
      try {
        const clientType = this.selectedClients.find(cliente => [1, 3, 4].includes(Number(cliente.cod_type_client)))?.cod_type_client
          ?? this.selectedClients[0]?.cod_type_client ?? 0
        const result = await this.service.search({ datas: { cf: this.cf.trim().toUpperCase() }, id_client: 0, cod_type_client: Number(clientType), cambio_rappresentante: true })
        if (this.isStandalone && result.exist) {
          this.duplicateIdentifier = this.cf.trim().toUpperCase()
          this.cf = ''
          this.searchInputKey++
          return
        }
        if (!this.isStandalone && !result.exist && allowCreate) {
          this.$emit('create-requested', this.cf.trim().toUpperCase())
          return
        }
        await this.applySearchResult(result)
      } catch (e) { this.error = this.message(e, 'Ricerca del rappresentante non riuscita.') } finally { this.busy = false }
    },
    async applySearchResult(result) {
        this.representative = result.exist ? result.rappresentante : { id: null, codfis: this.cf.trim().toUpperCase() }
        this.hasIdentityDocument = result.exist
          ? (await this.documentsService.getRepresentativeDocuments(this.representative.id)).some(documento => documento.ref === 'di')
          : false
        if (this.hasIdentityDocument) { this.identityFile = null; this.uploadKey++ }
        this.contacts = result.contatti || []
        if (!this.comuni.length || !this.toponimi.length) await this.loadOptions()
        this.supportsBypass = result.can_bypass_pec === true
        this.selectedContact = this.displayedContacts.find(contact => contact.selectable) || null
        this.excludeAlreadyAssignedContracts()
        this.newContact = !this.selectedContact
        this.searched = true
    },
    validCap(cap, cityId) {
      const city = this.comuni.find(city => String(city.id) === String(cityId))
      const cityCap = String(city?.cap || '').toUpperCase()
      return /^\d{5}$/.test(cap || '') && !!city && (/^\d{3}XX$/.test(cityCap) ? String(cap).startsWith(cityCap.slice(0, 3)) : cap === cityCap)
    },
    invalidAddressFields(contact) { return [{ key: 'toponimo', label: 'Toponimo' }, { key: 'indirizzo', label: 'Indirizzo' }, { key: 'civico', label: 'Civico' }, { key: 'cap', label: 'CAP' }].filter(field => contact.invalid_fields.includes(field.key)) },
    contactValue(contact, key) { const value = String(contact[key] || '').trim(); return value ? `${value}${contact.invalid_fields.includes(key) ? ' (Dato errato)' : ''}` : 'Mancante' },
    selectContact(contact) {
      if (contact.selectable) { this.selectedContact = contact; this.repairSource = null; this.newContact = false; return }
      this.selectedContact = null
      this.repairSource = contact
      this.pecBeforeBypass = contact.pec || ''
      this.contactForm = { ...emptyContact(), ...Object.fromEntries(Object.keys(emptyContact()).map(key => [key, contact[key] ?? emptyContact()[key]])), bypass_pec: this.supportsBypass && Number(contact.bypass_pec) === 1 }
      const city = this.comuni.find(city => String(city.id) === String(contact.cod_comune))
      this.province = city?.provincia || contact.provincia || null
      if (this.contactCapSettings.isFixed || !this.validCap(this.contactForm.cap, this.contactForm.cod_comune)) this.setCap()
      this.newContact = true
      this.$nextTick(() => this.scrollSelectedContact(contact.id))
    },
    scrollSelectedContact(id) {
      const list = this.$refs.contactList || this.$refs.selectionView?.$refs.contactList
      if (!list) return
      const card = Array.from(list.children).find(element => element.dataset.contactId === String(id))
      if (!card) return
      const containerRect = list.getBoundingClientRect()
      const cardRect = card.getBoundingClientRect()
      let offset = 0
      if (cardRect.top < containerRect.top) offset = cardRect.top - containerRect.top - 8
      else if (cardRect.bottom > containerRect.bottom) offset = cardRect.bottom - containerRect.bottom + 8
      if (offset) list.scrollTo({ top: list.scrollTop + offset, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
    },
    needsCompletion(key) { return this.repairSource?.invalid_fields?.includes(key) || false },
    startNewContact() { this.selectedContact = null; this.repairSource = null; this.pecBeforeBypass = ''; this.contactForm = emptyContact(); this.province = null; this.newContact = true },
    onBypassPecChange() {
      if (this.contactForm.bypass_pec) {
        this.pecBeforeBypass = this.contactForm.pec
        this.contactForm.pec = ''
      } else {
        this.contactForm.pec = this.originalPecLocked ? this.repairSource.pec : this.pecBeforeBypass
      }
    },
    address(c) { return `${c.toponimo || ''} ${c.indirizzo || ''} ${c.civico || ''}, ${c.cap || ''} - ${c.nome_comune || ''} ${c.provincia || ''}` },
    setCap() {
      const settings = this.contactCapSettings
      this.contactForm.cap = settings.isFixed ? settings.cap : settings.isWildcard ? settings.cap.slice(0, 3) : ''
    },
    onCapSuffixInput(event) {
      const suffix = event.target.value.replace(/\D/g, '').slice(0, 2)
      event.target.value = suffix
      this.contactForm.cap = this.contactCapSettings.cap.slice(0, 3) + suffix
    },
    message(e, fallback) { const data = e.response?.data; return data?.errors ? Object.values(data.errors).flat().join(' ') : data?.message || e.message || fallback },
    confirmSave() {
      if (this.saveDisabled) return
      if (this.isStandalone) { this.save(); return }
      const selectedIds = new Set(this.selectedContractIds.map(String))
      const contact = this.effectiveContact
      this.$confirm.require({
        group: 'conferma-cambio-rappresentante',
        header: 'Conferma cambio rappresentante',
        acceptLabel: 'Conferma e salva',
        rejectLabel: 'Torna alla modifica',
        rappresentante: {
          azione: this.representative.id ? 'Selezione' : 'Creazione',
          nome: this.representative.name || this.representative.ragione_sociale || [this.representative.nome, this.representative.cognome].filter(Boolean).join(' '),
          identificativo: this.representative.codfis || this.representative.piva || this.cf
        },
        contatto: (selectedIds.size || this.newContact) ? {
          azione: this.newContact ? (this.repairSource ? 'Completamento' : 'Creazione') : 'Selezione',
          indirizzo: [[contact.toponimo, contact.indirizzo, contact.civico].filter(Boolean).join(' '), [contact.cap, contact.nome_comune || contact.comune || this.comuni.find(city => String(city.id) === String(contact.cod_comune))?.name, contact.provincia || this.province].filter(Boolean).join(' ')].filter(Boolean).join(', '),
          cell: contact.cell,
          email: contact.email,
          pec: contact.pec,
          bypassPec: Boolean(contact.bypass_pec),
          motivazioneBypassPec: contact.motivazione_bypass_pec
        } : null,
        clienti: this.selectedClients.map(cliente => ({
          id: cliente.id,
          nome: cliente.nome,
          representativeUnchanged: this.clientHasRepresentative(cliente),
          numeroContratti: this.contractsOfClient(cliente.id).length,
          contratti: this.contractsOfClient(cliente.id)
            .filter(contract => selectedIds.has(String(contract.id)))
            .map(contract => ({ id: contract.id, code: contract.code, code_fornitura: contract.code_fornitura || contract.punti_prelievo, active: Boolean(contract.code_fornitura || contract.punti_prelievo) && Number(contract.active) === 1 }))
            .sort((a, b) => {
              const priority = contract => contract.active ? 0 : contract.code_fornitura ? 1 : 2
              return priority(a) - priority(b)
            })
        })),
        documenti: [this.identityFile && `Documento d'identità: ${this.identityFile.name}`, this.file && `Documento di nomina: ${this.file.name}`].filter(Boolean),
        accept: () => this.save()
      })
    },
    async save() {
      if (this.saveDisabled) return
      this.busy = true; this.error = ''
      try {
        const fd = new FormData()
        fd.append('rappresentante', JSON.stringify(this.representative.id ? this.representative : { ...this.representative, tipo_persona: this.kind === 'cf' ? 'fisica' : 'giuridica' }))
        if (this.isStandalone) fd.append('contatto', JSON.stringify(this.effectiveContact))
        else {
          fd.append('clienti_ids', JSON.stringify(this.selectedClients.map(cliente => cliente.id)))
          if (this.selectedContractIds.length || this.newContact) fd.append('contatto', JSON.stringify(this.effectiveContact))
          fd.append('contratti_ids', JSON.stringify(this.selectedContractIds.map(id => Number(id))))
          if (this.file) fd.append('nom', this.file)
        }
        if (this.identityFile) fd.append('di', this.identityFile)
        const result = this.isStandalone && this.embedded && this.createdResult
          ? this.createdResult
          : await (this.isStandalone ? this.service.createRepresentative(fd) : this.service.save(fd))
        if (!result.success) throw new Error(result.message || 'Salvataggio non riuscito')
        if (this.isStandalone) {
          if (this.embedded) {
            this.createdResult = result
            const identifier = this.cf.trim().toUpperCase()
            const lookup = await this.service.search({ datas: { cf: identifier }, id_client: 0, cod_type_client: 0, cambio_rappresentante: true })
            if (!lookup.exist || Number(lookup.rappresentante?.id) !== Number(result.rappresentante_id)) throw new Error('Rappresentante creato, ma non ancora disponibile nella verifica. Riprova senza creare un duplicato.')
            this.$emit('created', { ...result, identifier, lookup })
            return
          }
          if (!this.embedded) this.showOutcome({ success: true, rappresentanteId: result.rappresentante_id, documentMessage: typeof result.documento_identita === 'string' ? result.documento_identita : '' })
          this.$emit('created', result)
        } else {
          this.showOutcome({ success: true, clienti: Number(result.clienti_aggiornati) || 0, contratti: Number(result.contratti_aggiornati) || 0 })
          this.$emit('saved', result)
        }
      } catch (e) {
        const error = this.message(e, 'Salvataggio non riuscito. Nessuna modifica applicata.')
        if (this.embedded) this.error = error
        else this.showOutcome({ success: false, error })
      } finally { this.busy = false }
    }
  }
}
</script>

<style scoped>
.creation-step-left { display: flex; min-width: 0; min-height: 0; overflow: hidden; border: 1px solid #1a7f89; border-radius: 12px; background: var(--surface-0); }
.creation-step-left :deep(.inline-create-root .representative-flow) { border: 0; }
.creation-step-right { position: relative; background: var(--surface-100); }
.creation-step-right > section, .creation-step-right > .save-description { opacity: 0.5; pointer-events: none; }
.creation-step-notice { position: absolute; z-index: 1; top: 50%; left: 50%; transform: translate(-50%, -50%); display: flex; width: calc(100% - 2rem); justify-content: flex-start; padding: 1rem; border: 1px solid var(--yellow-500); box-shadow: 0 2px 10px #0003; line-height: 1.5; }
.inline-create-root { display: flex; flex-direction: column; min-height: 0; }
.inline-create-root > .representative-body { flex: 1 1 auto; min-height: 0; height: auto; }
.inline-create-footer { display: flex; flex-shrink: 0; justify-content: flex-end; gap: 0.75rem; padding: 0.5rem 1.25rem; }
.save-confirm-summary { width: 100%; max-height: 72vh; overflow-y: auto; line-height: 1.45; }
.save-outcome { display: flex; flex-direction: column; gap: 1rem; }
.save-outcome-message { width: 100%; justify-content: flex-start; }
.save-outcome-counts { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.save-outcome-counts p { flex: 1 1 180px; margin: 0; padding: 0.8rem; border: 1px solid var(--surface-border); border-radius: 6px; }
.save-confirm-summary h5 { margin: 0.7rem 0 0.3rem; }
.save-confirm-summary p { margin: 0.25rem 0; }
.save-confirm-summary section { border-top: 1px solid var(--surface-border); padding: 0.6rem 0; }
.save-confirm-detail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.65rem; }
.save-confirm-detail-grid > div { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; padding: 0.55rem 0.7rem; background: var(--surface-50); border-radius: 6px; overflow-wrap: anywhere; }
.save-confirm-detail-grid small { color: var(--text-color-secondary); }
.save-confirm-wide { grid-column: 1 / -1; }
.save-confirm-detail-grid--inline { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.save-confirm-detail-grid--inline > div { display: inline-flex; flex: 0 1 auto; flex-direction: row; align-items: baseline; gap: 0.35rem; padding: 0.4rem 0.55rem; }
.save-confirm-detail-grid--inline small { white-space: nowrap; }
.save-confirm-clients { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr)); gap: 0.65rem; }
.save-confirm-client { display: flex; flex-direction: column; gap: 0.35rem; padding: 0.7rem; border: 1px solid var(--surface-border); border-radius: 8px; overflow-wrap: anywhere; }
.save-confirm-contracts { display: flex; flex-wrap: wrap; gap: 0.3rem; }
.save-confirm-contracts .save-confirm-contract-chip { padding: 0.2rem 0.45rem; border-radius: 12px; font-size: 0.8rem; max-width: 100%; height: auto; line-height: 1.3; overflow-wrap: anywhere; }
.save-confirm-contracts .save-confirm-contract-chip.is-active { background: #d9f4df; color: #166534; }
.save-confirm-contracts .save-confirm-contract-chip.is-ceased { background: #fce1e1; color: #a11c1c; }
.representative-heading { text-align: center; margin: 0.5rem 0 1.5rem; }
.representative-heading h4 { margin: 0 0 0.75rem; }
.representative-heading p { color: var(--text-color-secondary); margin: 0; }
.representative-flow { border: 1px solid #1a7f89; border-radius: 20px; padding: 1.25rem; }
.client-selection { margin-bottom: 1rem; }
.client-selection-message { display: flex; width: 100%; justify-content: flex-start; margin: 0 0 0.75rem; line-height: 1.4; }
.client-selection label { display: block; margin-bottom: 0.35rem; }
.client-suggestion { display: flex; flex-wrap: wrap; column-gap: 0.75rem; row-gap: 0.15rem; align-items: baseline; white-space: normal; }
.client-suggestion strong { flex-basis: 100%; }
.client-suggestion.already-associated strong { color: #a11c1c; }
.client-suggestion span { font-size: 0.8rem; color: var(--text-color-secondary); }
.client-suggestion .client-suggestion-status { flex-basis: 100%; color: #15803d; font-weight: 600; }
.client-suggestion .client-suggestion-status.already-associated { color: #a11c1c; }
.representative-search { width: 50%; }
.representative-radio-group { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 12px; }
.representative-radio { display: flex; align-items: center; }
.representative-radio :deep(.p-radiobutton) { margin: 0; flex: 0 0 16px; width: 16px; height: 16px; }
.representative-radio :deep(.p-radiobutton-box) { width: 16px; height: 16px; border: 1px solid #ced4da; border-radius: 50%; background: #fff; }
.representative-radio :deep(.p-radiobutton.p-highlight .p-radiobutton-box) { border: 3px solid #2196f3; background: #fff; }
.representative-radio :deep(.p-radiobutton-icon) { display: none; }
.representative-radio label { margin: 0 0 0 6px !important; padding: 0; line-height: 16px; }
.uppercase-identifier { text-transform: uppercase; }
.identifier-input { width: 22ch; max-width: 100%; }
.representative-layout:not(.searched) .representative-search { width: fit-content; max-width: 100%; margin-left: auto; margin-right: auto; }
.contact-cap-prefix { min-width: 4rem; justify-content: center; font-weight: 700; background: #e9ecef; color: #495057; }
.representative-card { position: relative; max-width: 60%; margin: 0.25rem auto; border: 1px solid #696969; border-radius: 20px; padding: 1rem; text-align: center; }
.representative-card h4 { margin: 0.5rem 0 1rem; padding: 0 2rem; text-decoration: underline; overflow-wrap: anywhere; }
.representative-detail-link { display: inline-block; padding: 0.25rem 0.5rem; border-radius: 6px; color: inherit; text-decoration: underline; text-underline-offset: 0.2rem; }
.representative-detail-link:hover, .representative-detail-link:focus-visible { background: rgba(33, 81, 89, 0.1); color: #215159; }
.representative-card p { margin: 0.75rem 0; }
.remove-representative { position: absolute; top: 0.5rem; right: 0.5rem; }
.section-heading { flex: 0 0 auto; width: 100%; text-align: center; margin: 0.75rem 0 0.5rem; }
.contact-list { display: flex; flex-wrap: wrap; gap: 1rem; }
.contact-card { width: calc((100% - 2rem) / 3); min-height: 120px; justify-content: flex-start; text-align: left; color: #fff; background: #8ebac1; border: 1px solid #1a7f89; border-radius: 20px; padding: 1rem; white-space: normal; overflow-wrap: anywhere; font-size: 1rem; line-height: 1.4; transform: scale(0.94); transition: transform 0.2s, box-shadow 0.2s; }
.contact-card.selected { background: linear-gradient(135deg, #215159, #1a7f89); box-shadow: 0 0 10px #0003; transform: scale(1); }
.contact-card:not(:disabled):hover { transform: scale(1); background: #8ebac1; border-color: #1a7f89; color: #fff; }
.contact-data-list { display: flex; flex-direction: column; align-items: flex-start; }
.invalid-contact-data { background: #fff; color: #a11c1c; padding: 0 0.4rem; border-radius: 4px; font-weight: 700; }
.contact-field-to-complete :deep(.p-inputtext), .contact-field-to-complete :deep(.p-dropdown) { background: #fff8e1; border: 2px solid #d99500; box-shadow: 0 0 0 2px rgba(217, 149, 0, 0.12); }
.contact-field-to-complete :deep(.p-dropdown .p-inputtext) { border: none; box-shadow: none; }
.contact-card.duplicate-contact { position: relative; overflow: visible; animation: duplicate-contact-pulse 1.3s ease-in-out infinite; }
.duplicate-contact-badge { position: absolute; top: 50%; left: -1.5rem; transform: translateY(-50%); font-size: 1.5rem; pointer-events: none; }
@keyframes duplicate-contact-pulse { 0%, 100% { transform: scale(0.96); } 50% { transform: scale(1.02); } }
@media (prefers-reduced-motion: reduce) { .contact-card.duplicate-contact { animation: none; } }
.contact-card:focus-visible { outline: 2px solid #215159; outline-offset: 3px; }
.bypass-reason { color: #ff5252; font-weight: 700; }
.wizard-action { color: #fff; background: linear-gradient(135deg, #215159, #1a7f89); border-color: #1a7f89; }
.wizard-action:not(:disabled):hover { background: #215159; border-color: #215159; color: #fff; }
.return-search { width: 25%; min-width: 230px; border-radius: 20px; }
@media (max-width: 1100px) { .contact-card { width: calc((100% - 1rem) / 2); } .representative-search { width: 70%; } }
@media (max-width: 767px) { .representative-search, .representative-card, .contact-card { width: 100%; max-width: 100%; } .representative-flow { padding: 1rem 0.75rem; } .return-search { width: 100%; min-width: 0; } .contact-card { transform: none; } }
@media (prefers-reduced-motion: reduce) { .contact-card { transition: none; } }
label { display: block; margin-bottom: 0.5rem; }
.representative-layout.searched { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; align-items: start; padding: 0 0 0.25rem; box-sizing: border-box; }
.representative-layout.searched.standalone { grid-template-columns: minmax(0, 1fr); }
.representative-flow { padding: 0.85rem; border-radius: 12px; min-width: 0; }
.representative-documents { display: flex; flex-direction: column; min-width: 0; padding: 0.85rem; border: 1px solid #1a7f89; border-radius: 12px; }
.representative-documents section { margin-top: 0.5rem !important; }
.representative-documents :deep(.p-fileupload-buttonbar), .representative-documents :deep(.p-fileupload-content) { padding: 0.5rem; }
.representative-documents :deep(.p-fileupload-row > div) { padding: 0.35rem; }
.representative-heading { margin: 0 0 0.85rem; }
.representative-card { max-width: 100%; padding: 0.5rem; border-radius: 10px; }
.representative-card.mb-3 { margin-bottom: 0.35rem !important; }
.representative-card h4 { margin: 0 0 0.35rem; font-size: 1rem; }
.representative-card p { margin: 0.1rem 0; font-size: 0.85rem; }
.contact-list { flex-direction: column; flex-wrap: nowrap; align-items: flex-start; gap: 0.5rem; padding: 0.5rem 1.25rem; max-height: 310px; overflow-y: auto; overflow-x: hidden; }
.contact-card { width: 100%; max-width: 30rem; min-width: 0; min-height: 0; flex-shrink: 0; box-sizing: border-box; padding: 0.55rem 0.7rem; font-size: 0.85rem; line-height: 1.3; border-radius: 10px; transform: scale(0.97); }
.contact-data-list { min-width: 0; max-width: 100%; }
.contact-form-intro { text-align: center; }
.contact-form-intro h5 { margin: 0 0 0.25rem; }
.contact-form-intro small { display: block; }
.contact-form-intro hr { margin: 0.5rem 0 0.25rem; }
.contact-card.selected { transform: scale(1); }
.representative-flow .field { margin-bottom: 0.35rem; }
.representative-flow .field label { margin-bottom: 0.15rem; font-size: 0.8rem; }
.representative-flow .field :deep(.p-inputtext), .representative-flow .field :deep(.p-dropdown-label) { padding: 0.35rem 0.5rem; font-size: 0.85rem; }
.representative-search { width: 100%; }
.save-description { display: flex; width: 100%; justify-content: flex-start; margin: 0.75rem 0; line-height: 1.4; }
.representative-documents > .save-description { flex-shrink: 0; margin-top: auto; margin-bottom: 0; }
.client-contract-cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.6rem; margin: 0.6rem 0; }
.client-contract-item { position: relative; min-width: 0; }
.client-contract-remove { position: absolute; top: 0.45rem; right: 0.45rem; z-index: 1; display: inline-flex; align-items: center; justify-content: center; width: 1.6rem; height: 1.6rem; padding: 0; border: 0; border-radius: 50%; background: transparent; color: #b42318; cursor: pointer; }
.client-contract-remove:hover:not(:disabled), .client-contract-remove:focus-visible { background: #fee4e2; outline: 2px solid #b42318; }
.client-contract-remove:disabled { cursor: default; opacity: 0.5; }
.client-contract-card { position: relative; display: flex; flex-direction: column; gap: 0.3rem; width: 100%; min-height: 7rem; padding: 0.75rem 2.5rem 0.75rem 0.75rem; text-align: left; border: 1px solid #1a7f89; border-radius: 10px; background: #eef8f9; color: #215159; cursor: pointer; font: inherit; overflow-wrap: anywhere; }
.client-contract-open { position: absolute; bottom: 0.75rem; right: 0.75rem; font-size: 1rem; }
.client-contract-card:hover:not(:disabled), .client-contract-card:focus-visible { background: #dff1f3; outline: 2px solid #1a7f89; }
.client-contract-card:disabled { cursor: default; opacity: 1; background: var(--surface-100); border-color: var(--surface-border); color: var(--text-color); }
.client-contract-selected { margin-top: auto; font-size: 0.8rem; font-weight: 600; }
.client-representative-status { color: #15803d; font-size: 0.8rem; font-weight: 600; }
.client-representative-status.unchanged { color: #a11c1c; }
.client-contract-only-representative { display: block; width: 100%; min-width: 0; white-space: normal; color: var(--red-600, #c62828); font-weight: 600; }
@media (max-width: 1000px) { .representative-layout.searched { grid-template-columns: minmax(0, 1fr); } }
.representative-body { display: flex; flex-direction: column; width: 100%; min-width: 0; }
.representative-layout:not(.searched) { display: flex; flex: 1; justify-content: center; align-items: center; padding: 1.5rem 0; }
.representative-layout:not(.searched) .representative-flow { width: 100%; max-width: 40rem; padding: 2rem; background: var(--surface-0); border: 1px solid var(--surface-border); border-radius: 16px; box-shadow: 0 4px 18px #00000008; }
.search-heading { text-align: center; margin-bottom: 1.5rem; }
.search-heading > i { display: inline-flex; align-items: center; justify-content: center; width: 3rem; height: 3rem; background: #e5f4f5; color: #215159; border-radius: 50%; font-size: 1.25rem; margin-bottom: 0.75rem; }
.search-heading h4 { margin: 0 0 0.5rem; font-size: 1.15rem; }
.search-heading p { margin: 0; color: var(--text-color-secondary); line-height: 1.5; }
.representative-layout.searched .representative-flow, .representative-documents { background: var(--surface-0); }
.representative-layout.searched .representative-flow.searching-representative { justify-content: center; align-items: center; }
@media (max-width: 600px) { .representative-layout:not(.searched) { padding: 0.5rem 0; } .representative-layout:not(.searched) .representative-flow { padding: 1rem; } }
@media (min-width: 1001px) {
  .representative-body.has-result { height: 100%; min-height: 0; overflow: hidden; }
  .representative-layout.searched { flex: 1; min-height: 0; align-items: stretch; grid-template-rows: minmax(0, 1fr); }
  .representative-layout.searched .representative-flow,
  .representative-layout.searched .representative-documents { min-height: 0; overflow-y: auto; overflow-x: hidden; }
  .representative-layout.searched .representative-flow { display: flex; flex-direction: column; }
  .representative-layout.searched .contact-list { flex: 1 1 auto; min-height: 160px; max-height: none; }
}
.representative-card { width: 100%; max-width: none; box-sizing: border-box; flex-shrink: 0; }
.contact-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-auto-rows: minmax(8rem, max-content); align-content: flex-start; gap: 0.75rem; }
.contact-card { width: 100%; max-width: none; height: 100%; }
@media (max-width: 600px) {
  .contact-list, .client-contract-cards { grid-template-columns: minmax(0, 1fr); }
}
.document-upload-list { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 0.5rem; width: 100%; margin-top: auto; padding-top: 0.5rem; border-top: 1px solid var(--surface-border); }
.document-upload-list > section { flex: 1 1 18rem; min-width: 0; max-width: 100%; }
.document-upload-list > section h5 { margin: 0 0 0.35rem !important; line-height: 1.35; }
.document-upload-list :deep(.p-fileupload-buttonbar), .document-upload-list :deep(.p-fileupload-content) { padding: 0.5rem; }
.document-upload-list :deep(.p-fileupload-row > div) { padding: 0.35rem; }
.document-upload-list :deep(.p-fileupload-choose) { width: 100%; min-height: 2rem; justify-content: center; white-space: nowrap; }
.document-upload-list :deep(.p-fileupload-choose .p-button-label) { flex: 0 1 auto; }
.representative-flow :deep(input:disabled),
.representative-flow :deep(.p-dropdown.p-disabled) { pointer-events: auto; cursor: not-allowed; }
.representative-flow :deep(.p-dropdown.p-disabled *) { cursor: not-allowed !important; }
.contact-cap-prefix { cursor: not-allowed; user-select: none; }
@media (min-width: 1001px) {
  .representative-layout.searched .representative-flow { overflow-y: auto; overflow-x: hidden; }
  .representative-layout.searched .representative-flow > :not(.contact-list):not(.contact-completion-form) { flex-shrink: 0; }
  .representative-layout.searched .contact-list { flex: 1 1 0; min-height: 170px; overflow-y: auto; overscroll-behavior: contain; }
  .representative-layout.searched .contact-completion-form { flex: 0 1 auto; min-height: 0; max-height: 42%; overflow-y: auto; overflow-x: hidden; margin-left: 0; margin-right: 0; }
  .representative-layout.searched .has-contact-form:not(:has(.contact-list)) .contact-completion-form { max-height: none; flex: 1 1 auto; }
}
</style>
<style>
.cambio-rappresentante-dialog .p-dialog-header { padding: 0.6rem 1.25rem 0.35rem; }
.cambio-rappresentante-dialog .p-dialog-footer { padding: 0.5rem 1.25rem; }
</style>
